<template>

    <div class="support-plan-step-1">

        <!-- crumbs -->
        <div class="crumbs">
            <span class="crumb"
                  v-for="(item, index) in crumbs">
                <a href="#" @click="item.click()">
                    {{ item.text }}
                </a>
                <span class="crumb-separator"
                      v-if="index < crumbs.length - 1">
                    /
                </span>
            </span>
        </div>

        <!-- top level: list main categories -->
        <div v-if="!selectedCategory" class="plan-categories plan-categories__level-one">
            <h4 class="step-title">Categories</h4>
            <div class="plan-categories__list">
                <b-btn block size="lg" variant="primary"
                       v-for="cat in categories"
                       @click="selectCategory(cat)">
                    {{ cat.title }} ({{ getCategoryStrategyCount(cat) }})
                </b-btn>
            </div>
        </div>

        <div v-if="selectedCategory" class="plan-categories plan-categories__level-two">
            <!-- second level: list subjects within selected category -->
            <div v-if="!selectedSubject">
                <h4 class="step-title">{{ selectedCategory.title }}</h4>
                <div class="plan-categories__list">
                    <b-btn block size="lg" variant="primary"
                           v-for="subject in selectedCategory.subjects"
                           @click="selectSubject(subject)">
                        {{ subject.title }} ({{ getSubjectStrategyCount(subject) }})
                    </b-btn>
                </div>
            </div>
            <div class="plan-categories__list">
                <!-- third level: list strategies within selected subject -->
                <div v-if="selectedSubject">
                    <div v-if="!selectedStrategyId && !customStrategy">
                        <h4 class="step-title">{{ selectedSubject.title }}</h4>

                        <b-btn block variant="primary"
                               id="`strategy-button-${sid}`"
                               v-for="sid in selectedSubject.strategies"
                               @click="selectStrategy(sid)"
                               v-b-popover.hover.top.html="getStrategy(sid).text">
                            {{ getStrategy(sid).title }}
                            <b-badge variant="primary" class="ml-2 support-plan__in-cart"
                                     v-if="strategyIsInCart(sid)">
                                In Cart
                            </b-badge>
                        </b-btn>
                        <b-btn block size="lg" variant="success"
                               @click="createCustomStrategy()">
                            Add a Custom Strategy
                        </b-btn>
                    </div>
                </div>
                <!-- fourth level: display selected strategy -->
                <div v-if="selectedStrategyId !== null" class="strategy__content">
                    <h3>
                        {{ getStrategy(selectedStrategyId).title }}
                    </h3>
                    <p class="show-line-breaks">
                        {{ getStrategy(selectedStrategyId).text }}
                    </p>
                    <b-row>
                        <b-col sm="12" class="strategy__notes">
                            <h5>Your Notes</h5>
                            <b-form-textarea v-model="notes">
                            </b-form-textarea>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col sm="6" class="strategy__citation">
                            <h5>Citations</h5>
                            <p v-for="aid in getStrategy(selectedStrategyId).annotations">
                                {{ getAnnotation(aid).text }}
                            </p>
                        </b-col>
                        <b-col sm="6" class="strategy__links">
                            <h5>Links</h5>
                            <p v-html="getStrategy(selectedStrategyId).links">
                            </p>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col class="text-center">
                            <b-btn variant="danger"
                                   @click="clearSelectedStrategy()">
                                Back
                            </b-btn>
                        </b-col>
                        <b-col class="text-center">
                            <b-btn variant="primary"
                                   @click="addStrategy()">
                                Add Strategy
                            </b-btn>
                        </b-col>
                    </b-row>
                </div>
                <!-- fourth level: edit custom strategy -->
                <div v-if="customStrategy" class="strategy__content">
                    <h3>Create Custom Strategy</h3>

                    <b-form-group label="Strategy Title">
                        <b-form-input type="text"
                                      v-model="customStrategy.title"></b-form-input>
                    </b-form-group>

                    <b-form-group label="Text">
                        <b-form-textarea rows="10"
                                         v-model="customStrategy.text"></b-form-textarea>
                    </b-form-group>

                    <b-form-group label="Your Notes">
                        <b-form-textarea rows="5"
                                         v-model="notes"></b-form-textarea>
                    </b-form-group>

                    <b-form-group label="Links">
                        <b-form-textarea rows="5"
                                         v-model="customStrategy.links"></b-form-textarea>
                    </b-form-group>

                    <b-row>
                        <b-col class="text-center">
                            <b-btn variant="danger"
                                   @click="clearCustomStrategy()">
                                Back
                            </b-btn>
                        </b-col>
                        <b-col class="text-center">
                            <b-btn variant="primary"
                                   @click="addCustomStrategy()">
                                Add Strategy
                            </b-btn>
                        </b-col>
                    </b-row>

                </div>
            </div>
        </div>

    </div>

</template>

<script>

    import Vue from 'vue'
    import ToolMixin from '@/providers/ToolMixin'
    import SupportPlanMixin from "../providers/SupportPlanMixin";

    export default Vue.extend({
        name: "SupportPlanStep1",
        mixins: [ToolMixin, SupportPlanMixin],
        data() {
            return {
                selectedCategory: null,
                selectedSubject: null,
                selectedStrategyId: null,
                notes: null,
                customStrategy: null,
            }
        },
        computed: {
            categories() {
                return _.filter(this.$store.state.config.supportPlan.statementCategories, (cat) => {
                    return cat.enabled
                })
            },
            crumbs() {
                const items = []
                // clicking on a crumb link deselects the relevant category/subject/strategy, and clears the notes
                if (this.selectedCategory) {
                    items.push({
                        text: 'Categories',
                        click: this.clearSelectedCategory
                    })

                    if (this.selectedSubject) {
                        items.push({
                            text: this.selectedCategory.title,
                            click: this.clearSelectedSubject
                        })

                        if (this.selectedStrategyId) {
                            items.push({
                                text: this.selectedSubject.title,
                                click: this.clearSelectedStrategy
                            })
                        }
                    }
                }
                return items
            }
        },
        methods: {
            getCategoryStrategyCount(category) {
                return _.reduce(category.subjects, (sum, subject) => {
                    return sum + subject.strategies.length
                }, 0)
            },
            getSubjectStrategyCount(subject) {
                return subject.strategies.length
            },
            strategyIsInCart(sid) {
                return null != _.find(this.docData.strategies, (s) => {
                    return s.strategy.id == sid
                })
            },
            getStrategy(id) {
                const strategy = _.find(this.config.supportPlan.strategies, (s) => s.id == id)
                if (strategy) {
                    return strategy
                } else {
                    return {title: 'DELETED STRATEGY'}
                }
            },
            async selectCategory(cat) {
                if (await this.clearCustomStrategy()) {
                    this.selectedCategory = cat
                }
            },
            async selectSubject(subject) {
                if (await this.clearCustomStrategy()) {
                    this.selectedSubject = subject
                }
            },
            async selectStrategy(sid) {
                if (await this.clearCustomStrategy()) {
                    this.selectedStrategyId = sid
                }
            },
            async clearSelectedCategory() {
                if (await this.clearCustomStrategy()) {
                    this.selectedCategory = null
                    this.selectedSubject = null
                    this.selectedStrategyId = null
                    this.notes = null
                }
            },
            async clearSelectedSubject() {
                if (await this.clearCustomStrategy()) {
                    this.selectedSubject = null
                    this.selectedStrategyId = null
                    this.notes = null
                }
            },
            async clearSelectedStrategy() {
                if (await this.clearCustomStrategy()) {
                    this.selectedStrategyId = null
                    this.notes = null
                }
            },
            // called in response to various clicks, to make sure that the user wants to abandon a custom strategy
            async clearCustomStrategy() {
                if (this.customStrategy) {
                    const ok = await this.$bvModal.msgBoxConfirm('Are you sure? This action will permanently delete any text you have entered for this custom strategy!')
                    if (ok) {
                        this.selectedStrategyId = null
                        this.notes = null
                        this.customStrategy = null
                        return true
                    } else {
                        return false // cancel clicked
                    }
                } else {
                    return true // nothing to clear
                }
            },
            addStrategy() {
                // NB: must update collection thus in order for the docData watcher to fire
                // when the array is initially empty! (see App::watch)
                Vue.set(this.docData, 'strategies', _.concat(
                    this.docData.strategies ? this.docData.strategies : [],
                    [{
                        categoryTitle: this.selectedCategory.title,
                        subjectTitle: this.selectedSubject.title,
                        strategy: this.getStrategy(this.selectedStrategyId),
                        notes: this.notes
                    }]
                ))

                this.clearSelectedStrategy() // stay in same subject
            },
            createCustomStrategy() {
                this.customStrategy = {
                    title: 'Custom Strategy',
                    text: '',
                    // NB: annotations not supported for custom strategies
                    // (no easy way for user to select from the annotation library, as we can do in the admin UI)
                    links: '',
                    isCustom: true
                }
                window.scrollTo(0, 300)
            },
            addCustomStrategy() {
                Vue.set(this.docData, 'strategies', _.concat(
                    this.docData.strategies ? this.docData.strategies : [],
                    [{
                        categoryTitle: this.selectedCategory.title,
                        subjectTitle: this.selectedSubject.title,
                        strategy: this.customStrategy,
                        notes: this.notes
                    }]
                ))

                this.customStrategy = null
            },
            deleteStrategy(item) {
                this.docData.strategies = _.without(this.docData.strategies, item)
            }
        }
    })

</script>

<style lang="scss">

    @import 'sass/variables_mixins';

    .plan-categories {
        max-width: 700px;
        margin: 0 auto;
    }

    .strategy__content {
        margin-top: 2em;
    }

    .strategy__citation, .strategy__notes {
        margin-bottom: 1em;
    }

    .plan-categories__list {
        background-color: $light-neutral-bg;
        padding: 2em;
    }

    .popover-body {
        max-height: 400px;
        overflow-y: hidden;
        white-space: pre-line;
    }

    .support-plan__in-cart {
        float: right;
        background-color: $pink;
        padding: .2rem .5rem;
    }

</style>